/**
 * Web Storage Service for Salesforce Implementation
 * Handles persistent storage of JWT tokens, configuration, and conversation data
 */

import { STORAGE_KEYS } from './constants';

// Determine the best storage type available
const determineStorageType = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      // Test if localStorage is actually writable
      const testKey = '__salesforce_storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    }
  } catch (e) {
    console.warn('localStorage not available, falling back to sessionStorage');
  }

  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const testKey = '__salesforce_storage_test__';
      window.sessionStorage.setItem(testKey, 'test');
      window.sessionStorage.removeItem(testKey);
      return window.sessionStorage;
    }
  } catch (e) {
    console.warn('sessionStorage not available');
  }

  return null;
};

// Get the storage instance
const getStorage = () => {
  const storage = determineStorageType();
  if (!storage) {
    console.error('No web storage available for Salesforce implementation');
  }
  return storage;
};

// Generic storage operations
export const setItemInWebStorage = (key, value) => {
  const storage = getStorage();
  if (!storage) return false;

  try {
    const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
    storage.setItem(`salesforce_${key}`, serializedValue);
    return true;
  } catch (error) {
    console.error('Failed to set item in web storage:', error);
    return false;
  }
};

export const getItemInWebStorage = (key) => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const value = storage.getItem(`salesforce_${key}`);
    if (value === null) return null;

    // Try to parse as JSON, if that fails return as string
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  } catch (error) {
    console.error('Failed to get item from web storage:', error);
    return null;
  }
};

export const removeItemFromWebStorage = (key) => {
  const storage = getStorage();
  if (!storage) return false;

  try {
    storage.removeItem(`salesforce_${key}`);
    return true;
  } catch (error) {
    console.error('Failed to remove item from web storage:', error);
    return false;
  }
};

export const clearSalesforceWebStorage = () => {
  const storage = getStorage();
  if (!storage) return false;

  try {
    // Clear all Salesforce-related items
    Object.values(STORAGE_KEYS).forEach(key => {
      storage.removeItem(`salesforce_${key}`);
    });
    // Clear user context for authenticated sessions
    clearUserContext();
    return true;
  } catch (error) {
    console.error('Failed to clear Salesforce web storage:', error);
    return false;
  }
};

// Specific Salesforce data storage functions
export const storeJwt = (jwt) => setItemInWebStorage(STORAGE_KEYS.JWT, jwt);
export const getJwt = () => getItemInWebStorage(STORAGE_KEYS.JWT);
export const clearJwt = () => removeItemFromWebStorage(STORAGE_KEYS.JWT);

export const storeOrganizationId = (orgId) => setItemInWebStorage(STORAGE_KEYS.ORGANIZATION_ID, orgId);
export const getOrganizationId = () => getItemInWebStorage(STORAGE_KEYS.ORGANIZATION_ID);

export const storeDeploymentDeveloperName = (name) => setItemInWebStorage(STORAGE_KEYS.DEPLOYMENT_DEVELOPER_NAME, name);
export const getDeploymentDeveloperName = () => getItemInWebStorage(STORAGE_KEYS.DEPLOYMENT_DEVELOPER_NAME);

export const storeMessagingUrl = (url) => setItemInWebStorage(STORAGE_KEYS.MESSAGING_URL, url);
export const getMessagingUrl = () => getItemInWebStorage(STORAGE_KEYS.MESSAGING_URL);

export const storeConversationId = (conversationId) => setItemInWebStorage(STORAGE_KEYS.CONVERSATION_ID, conversationId);
export const getConversationId = () => getItemInWebStorage(STORAGE_KEYS.CONVERSATION_ID);
export const clearConversationId = () => removeItemFromWebStorage(STORAGE_KEYS.CONVERSATION_ID);

export const storeConversationStartTime = (timestamp) => setItemInWebStorage(STORAGE_KEYS.CONVERSATION_START_TIME, timestamp);
export const getConversationStartTime = () => getItemInWebStorage(STORAGE_KEYS.CONVERSATION_START_TIME);
export const clearConversationStartTime = () => removeItemFromWebStorage(STORAGE_KEYS.CONVERSATION_START_TIME);

export const storeLastEventId = (eventId) => setItemInWebStorage(STORAGE_KEYS.LAST_EVENT_ID, eventId);
export const getLastEventId = () => getItemInWebStorage(STORAGE_KEYS.LAST_EVENT_ID);
export const clearLastEventId = () => removeItemFromWebStorage(STORAGE_KEYS.LAST_EVENT_ID);

// User context storage for authenticated conversations
const USER_CONTEXT_KEY = 'user_context';

/**
 * Store user context for authenticated conversations
 * NOTE: Only store non-sensitive identifiers (NOT the Cognito token)
 * @param {Object} userContext - User information
 * @param {string} userContext.userId - User ID
 * @param {string} userContext.email - User email
 * @param {string} userContext.name - User display name
 */
export const storeUserContext = (userContext) => {
  try {
    const safeContext = {
      userId: userContext.userId || '',
      email: userContext.email || '',
      name: userContext.name || ''
    };
    return setItemInWebStorage(USER_CONTEXT_KEY, safeContext);
  } catch (error) {
    console.error('[Storage] Failed to store user context:', error);
    return false;
  }
};

/**
 * Get stored user context
 * @returns {Object|null} User context or null if not found
 */
export const getUserContext = () => {
  try {
    return getItemInWebStorage(USER_CONTEXT_KEY);
  } catch (error) {
    console.error('[Storage] Failed to get user context:', error);
    return null;
  }
};

/**
 * Clear user context from storage
 */
export const clearUserContext = () => {
  try {
    return removeItemFromWebStorage(USER_CONTEXT_KEY);
  } catch (error) {
    console.error('[Storage] Failed to clear user context:', error);
    return false;
  }
};

// Store Salesforce configuration
export const storeSalesforceConfig = (config) => {
  const { ORGANIZATION_ID, DEVELOPER_NAME, API_BASE_URL } = config;
  return Promise.all([
    storeOrganizationId(ORGANIZATION_ID),
    storeDeploymentDeveloperName(DEVELOPER_NAME),
    storeMessagingUrl(API_BASE_URL)
  ]);
};

// Get stored Salesforce configuration
export const getStoredSalesforceConfig = () => {
  const organizationId = getOrganizationId();
  const deploymentDeveloperName = getDeploymentDeveloperName();
  const messagingUrl = getMessagingUrl();
  
  // Return null if no config is stored
  if (!organizationId || !deploymentDeveloperName || !messagingUrl) {
    return null;
  }
  
  return {
    ORGANIZATION_ID: organizationId,
    DEVELOPER_NAME: deploymentDeveloperName,
    API_BASE_URL: messagingUrl
  };
};

// Check if web storage is available
export const isWebStorageAvailable = () => {
  return getStorage() !== null;
};

/**
 * Validate restoration data before attempting conversation restoration
 * 
 * NOTE: For userId-based (authenticated) flow, we no longer require stored conversationId
 * The /end-user endpoint will return the active conversation for the authenticated user
 * based on their Cognito sub (userId) in the JWT.
 * 
 * This function now primarily validates that we have a valid user context.
 * 
 * @returns {Object} { isValid: boolean, jwt?, conversationId?, userContext?, reason? }
 */
export const validateRestorationData = () => {
  const jwt = getJwt();
  const conversationId = getConversationId();
  const userContext = getUserContext();
  
  const result = {
    isValid: false,
    jwt,
    conversationId,  // May be null for cross-device scenarios
    userContext,
    reason: ''
  };

  // Validate user context for authenticated sessions
  // This is the PRIMARY requirement - conversationId is optional
  if (!userContext || !userContext.userId) {
    result.reason = 'Missing or invalid user context for authenticated session';
    return result;
  }

  // JWT is nice to have locally but not strictly required
  // getAuthenticatedAccessToken will fetch a new one if needed
  if (jwt) {
    result.jwt = jwt;
  }

  result.isValid = true;
  return result;
};

// Export alias for backward compatibility
export const setJwt = storeJwt;